import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
export function Support() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    issueType: '',
    urgency: '',
    product: '',
    description: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [ticketId] = useState(() => Math.random().toString(36).substr(2, 9).toUpperCase());
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter your name';
    if (!formData.email.trim()) newErrors.email = 'Please enter a valid email address';else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.company.trim()) newErrors.company = 'Please provide your company or account name';
    if (!formData.issueType) newErrors.issueType = 'Please select an issue type';
    if (!formData.urgency) newErrors.urgency = 'Please select urgency level';
    if (!formData.product.trim()) newErrors.product = 'Please specify the affected system';
    if (!formData.description.trim()) newErrors.description = 'Please describe the issue';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setSubmitted(true);
      // In production, send to backend
      console.log('Support request submitted:', formData);
    }
  };
  const getResponseTime = () => {
    switch (formData.urgency) {
      case 'critical':
        return '1 hour';
      case 'high':
        return '4 hours';
      case 'medium':
        return '1 business day';
      case 'low':
        return '2 business days';
      default:
        return 'soon';
    }
  };
  if (submitted) {
    return <div className="min-h-screen w-full bg-white flex items-center justify-center" data-id="element-354">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="max-w-2xl mx-auto px-4 text-center" data-id="element-355">
          <div className="bg-surface rounded-2xl p-12" data-id="element-356">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-357">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-358">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-359" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-360">
              Support Request Received
            </h2>
            <div className="bg-white rounded-lg p-4 mb-6 inline-block" data-id="element-361">
              <p className="text-sm text-gray-600 mb-1" data-id="element-362">Ticket Number</p>
              <p className="text-2xl font-bold text-primary" data-id="element-363">#{ticketId}</p>
            </div>
            <p className="text-lg text-gray-600" data-id="element-364">
              A technician will review your issue and respond within{' '}
              {getResponseTime()} based on the urgency level. You'll receive
              updates at {formData.email}.
            </p>
          </div>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen w-full bg-white" data-id="element-365">
      <section className="py-20" data-id="element-366">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-367">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center mb-12" data-id="element-368">
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6" data-id="element-369">
              Client Support
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto" data-id="element-370">
              Need help with your Magnus Software system? Submit a support
              request and our technical team will assist you promptly.
            </p>
          </motion.div>

          <motion.form initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} onSubmit={handleSubmit} className="bg-surface rounded-2xl p-8 md:p-12" data-id="element-371">
            <div className="space-y-6" data-id="element-372">
              <Input label="Full Name" type="text" placeholder="John Smith" value={formData.name} onChange={value => setFormData({
              ...formData,
              name: value
            })} error={errors.name} required data-id="element-373" />

              <Input label="Email" type="email" placeholder="john@company.com" value={formData.email} onChange={value => setFormData({
              ...formData,
              email: value
            })} error={errors.email} required data-id="element-374" />

              <Input label="Phone Number" type="tel" placeholder="(555) 123-4567" value={formData.phone} onChange={value => setFormData({
              ...formData,
              phone: value
            })} data-id="element-375" />

              <Input label="Company or Account Name" type="text" placeholder="Your company name or project identifier" value={formData.company} onChange={value => setFormData({
              ...formData,
              company: value
            })} error={errors.company} helperText="This helps us locate your account quickly." required data-id="element-376" />

              <div className="flex flex-col gap-1.5" data-id="element-377">
                <label className="text-sm font-semibold text-ink" data-id="element-378">
                  What type of issue are you experiencing?{' '}
                  <span className="text-red-500" data-id="element-379">*</span>
                </label>
                <select value={formData.issueType} onChange={e => setFormData({
                ...formData,
                issueType: e.target.value
              })} className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white ${errors.issueType ? 'border-red-500' : 'border-neutral'}`} data-id="element-380">
                  <option value="" data-id="element-381">Select issue type</option>
                  <option value="bug" data-id="element-382">Bug or error</option>
                  <option value="performance" data-id="element-383">Performance issue</option>
                  <option value="feature" data-id="element-384">Feature request</option>
                  <option value="access" data-id="element-385">Access or login problem</option>
                  <option value="data" data-id="element-386">Data or integration issue</option>
                  <option value="question" data-id="element-387">General question</option>
                  <option value="other" data-id="element-388">Other</option>
                </select>
                {errors.issueType && <p className="text-sm text-red-500" data-id="element-389">{errors.issueType}</p>}
              </div>

              <div className="flex flex-col gap-3" data-id="element-390">
                <label className="text-sm font-semibold text-ink" data-id="element-391">
                  Urgency Level <span className="text-red-500" data-id="element-392">*</span>
                </label>
                <div className="space-y-2" data-id="element-393">
                  {[{
                  value: 'critical',
                  label: 'Critical',
                  desc: 'System down, blocking work'
                }, {
                  value: 'high',
                  label: 'High',
                  desc: 'Significant impact, workaround available'
                }, {
                  value: 'medium',
                  label: 'Medium',
                  desc: 'Affecting some users or features'
                }, {
                  value: 'low',
                  label: 'Low',
                  desc: 'Minor issue, no immediate impact'
                }].map(option => <label key={option.value} className={`flex items-start gap-3 p-4 bg-white rounded-lg border cursor-pointer transition-colors ${formData.urgency === option.value ? 'border-primary bg-blue-50' : 'border-neutral hover:border-primary'}`} data-id="element-394">
                      <input type="radio" name="urgency" value={option.value} checked={formData.urgency === option.value} onChange={e => setFormData({
                    ...formData,
                    urgency: e.target.value
                  })} className="mt-1 w-4 h-4 text-primary border-neutral focus:ring-2 focus:ring-primary" data-id="element-395" />
                      <div data-id="element-396">
                        <p className="font-semibold text-ink" data-id="element-397">{option.label}</p>
                        <p className="text-sm text-gray-600" data-id="element-398">{option.desc}</p>
                      </div>
                    </label>)}
                </div>
                {errors.urgency && <p className="text-sm text-red-500" data-id="element-399">{errors.urgency}</p>}
              </div>

              <Input label="Which product or system is affected?" type="text" placeholder="e.g., Customer Portal, Inventory System, Mobile App" value={formData.product} onChange={value => setFormData({
              ...formData,
              product: value
            })} error={errors.product} required data-id="element-400" />

              <Input label="Describe the issue" type="textarea" placeholder="Please provide details about what's happening, any error messages, and steps to reproduce if applicable..." value={formData.description} onChange={value => setFormData({
              ...formData,
              description: value
            })} error={errors.description} helperText="The more detail you provide, the faster we can help." rows={6} required data-id="element-401" />

              <div className="pt-4" data-id="element-402">
                <Button type="submit" variant="primary" size="lg" className="w-full" data-id="element-403">
                  Submit Support Request
                </Button>
              </div>
            </div>
          </motion.form>
        </div>
      </section>
    </div>;
}